<template>
    <div>
        <component :is="card ? 'card' : 'div'"
            v-if="taskId === undefined && (!onlyShowIfHasTasks || filteredTasks.length)">
            <template #header>
                <component :is="size == 'sm' ? 'h4' : 'h1'"
                    :class="(size == 'sm' ? 'card-subtitle ' : 'card-title ') + titleClass">
                    <i v-if="!!icon" :class="icon"></i>
                    <template v-if="title">
                        {{ title }}
                    </template>
                    <template v-else-if="template">
                        Task Templates
                    </template>
                    <template v-else>
                        {{ scheduled ? "Scheduled " : "" }}
                        Tasks
                    </template>
                </component>
            </template>

            <div v-if="!hideFilters" class="filters">
                <b-form-group v-if="!scheduled && !template && size !== 'sm'" label="Assigned To">
                    <b-select v-model="assignedToFilter" size="sm">
                        <option v-for="opt in assignedToFilterOptions"
                            :key="opt.value" :value="opt.value" :title="opt.title">
                            {{ opt.text }}
                        </option>
                        <optgroup v-if="teams.length" label="Teams">
                            <option v-for="t in teams" :key="t.id" :value="`team-${t.id}`">
                                {{ t.name }}
                            </option>
                        </optgroup>
                    </b-select>
                </b-form-group>

                <b-form-group v-if="!scheduled && !template" label="Status">
                    <b-select v-model="statusFilter" :options="statusFilterOptions" size="sm" />
                </b-form-group>

                <!-- v-show (not v-if): the select must mount at initial render so BootstrapVue reflects the
                     completedToday value via a normal reactive update; a late v-if mount leaves it blank. -->
                <b-form-group v-show="showCompletedTodayFilter && !scheduled && !template" label="Completed">
                    <b-select v-model="completedTodayFilter" :options="completedTodayFilterOptions" size="sm" />
                </b-form-group>

                <b-form-group v-if="!scheduled && !template && size !== 'sm'" label="Due">
                    <b-select v-model="dueFilter" :options="dueFilterOptions" size="sm" />
                </b-form-group>

                <b-form-group v-if="!scheduled && !template && size !== 'sm'" label="Watched By">
                    <b-select v-model="watchedByFilter" :options="watchedByFilterOptions" size="sm" />
                </b-form-group>

                <b-form-group v-if="scheduled || template" label="Enabled">
                    <b-select v-model="enabledFilter" :options="enabledFilterOptions" size="sm" />
                </b-form-group>

                <b-form-group label="Store">
                    <SearchComponent :key="storeFilter?.id" v-model="storeFilter" :hide-label="true"
                        search-u-r-l="/Store" :params="{active: true}" :prepopulate="true" :clearable="true" size="sm" />
                </b-form-group>

                <b-form-group v-if="!scheduled && !template && size !== 'sm'" label="Source Template">
                    <SearchComponent :key="templateFilter?.id" v-model="templateFilter" :hide-label="true"
                        search-u-r-l="/Task" :prepopulate="true" :clearable="true" size="sm" :truncate="true"
                        :params="{ template: true, enabled: true, resultsOnly: true }" />
                </b-form-group>

                <b-form-group v-if="!scheduled && !template && size !== 'sm'" label="Unanswered Questions">
                    <b-select v-model="unansweredQuestionsFilter" :options="unansweredQuestionsFilterOptions" size="sm" />
                </b-form-group>

                <b-form-group>
                    <label>
                        Search
                        <HelpPopup :icon-muted="true">
                            You can search for:
                            <ul>
                                <li>Task ID (e.g., "12345")</li>
                                <li>Task title (partial match)</li>
                            </ul>
                        </HelpPopup>
                    </label>
                    <b-input v-model="searchFilter" type="search" size="sm" lazy maxlength="300" />
                </b-form-group>
            </div>

            <div>
                <b-table ref="table" :items="filteredTasks" :fields="fields" :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc" selectable :select-mode="selectMode" :no-local-sorting="true"
                    @row-selected="onSelect" show-empty
                    :empty-text="scheduled ? 'No Scheduled Tasks Found' : template ? 'No Templates Found' : 'No Tasks Found'"
                    :responsive="compact ? true : 'sm'"
                    :class="{ 'compact-table': compact }">
                    <template #cell(lock)="data">
                        <span v-if="data.item.lockedBy" v-b-tooltip.hover :title="`Locked by ${data.item.lockedBy}`">
                            <b-icon icon="lock-fill" variant="warning" />
                        </span>
                    </template>
                    <template #cell(watching)="data">
                        <i v-if="data.value"
                            :class="{ 'fa text-muted': data.value === 'team', 'fas': data.value === 'me', 'fa-eye fa-fw': true }"
                            :title="data.value === 'me' ? 'I\'m Watching' : 'Team is Watching'" />

                        <a v-if="!selectedTasks.find(t => t.id === data.item.id)" href="#"
                            @click.prevent="selectMode = 'multi'; $nextTick(() => data.selectRow())">
                            <i class="far fa-fw fa-square select" />

                        </a>
                        <a v-else href="#" @click.prevent="data.unselectRow">
                            <i class="fa fa-fw fa-check-square select" />
                        </a>
                    </template>
                    <template #cell(title)="data">
                        <strong>
                            <a :href="`/processing/tasks/${data.item.id}`" @click.prevent="activeTaskId = data.item.id">
                                {{ data.value }}
                            </a>
                        </strong>
                    </template>
                    <template #cell(assignments)="data">
                        <div class="badges">
                            <b-badge v-for="t in data.value.filter(d => d.teamId)" :key="t.teamId" variant="primary"
                                class="m-0" :title="'Assigned to Team: ' + t.teamName">
                                <i class="fa fa-users" />
                                {{ t.teamName }}
                            </b-badge>
                            <b-badge v-for="t in data.value.filter(d => d.userName)" :key="t.userName" variant="info"
                                class="m-0" :title="'Assigned to User: ' + t.userName">
                                <i class="fa fa-user" />
                                {{ formatName(t.userName) }}
                            </b-badge>
                            <b-badge v-for="t in data.value.filter(d => d.patientId)" :key="t.patientId"
                                variant="warning" class="m-0"
                                :title="'Assigned to Patient: ' + t.patient.firstName + ' ' + t.patient.lastName">
                                <i class="far fa-user" />
                                {{ t.patient.firstName + ' ' + t.patient.lastName }}
                            </b-badge>
                            <b-badge v-for="t in data.value.filter(d => d.prescriberId)" :key="t.prescriberId"
                                variant="danger" class="m-0"
                                :title="'Assigned to Prescriber: ' + t.prescriber.firstName + ' ' + t.prescriber.lastName">
                                <i class="fa fa-notes-medical" />
                                {{ t.prescriber.firstName + ' ' + t.prescriber.lastName }}
                            </b-badge>
                            <b-badge v-for="t in data.value.filter(d => d.brandPortalUserEmail)"
                                :key="'bp-' + t.brandPortalUserEmail" variant="success" class="m-0"
                                :title="'Assigned to Brand Portal User: ' + t.brandPortalUserEmail">
                                <i class="fa fa-globe" />
                                {{ t.brandPortalUserEmail }}
                            </b-badge>
                        </div>
                    </template>
                    <template #cell(status)="data">
                        <TaskStatusBadge :value="data.value" />
                    </template>
                    <template #cell(dueDate)="data">
                        <span :class="isPastDue(data.item) ? 'text-danger font-weight-bold' : ''">
                            {{ data.value }}
                        </span>
                    </template>
                    <template #cell(dates)="data">
                        <div>
                            <span :class="isPastDue(data.item) ? 'text-danger font-weight-bold' : ''">
                                {{ formatDueDate(data.item.dueDate, data.item.dueAtTime) }}
                            </span>
                            <div v-if="isFutureStart(data.item)" class="text-muted" style="font-size: 0.75rem; line-height: 1.2; margin-top: 2px;"
                                :title="'Starts ' + formatDateOnly(data.item.startDate)">
                                Starts {{ moment.utc(data.item.startDate).fromNow() }}
                            </div>
                        </div>
                    </template>
                    <template #cell(patients)="data">
                        <div class="badges">
                            <b-badge v-for="p in (data.value ?? [])" variant="outline-primary" :key="p.id"
                                :title="p.firstName + ' ' + p.lastName">
                                {{ p.firstName.substring(0, 1) + p.lastName.substring(0, 1) }}
                            </b-badge>
                        </div>
                    </template>
                    <template #cell(prescriptions)="data">
                        <div class="badges">
                            <b-badge v-for="p in (data.value ?? [])" variant="outline-primary"
                                :key="p.storeID + '-' + p.rxNumber + '-' + p.rfNumber">
                                {{ p.storeID + "-" + p.rxNumber + "-" + p.rfNumber }}
                            </b-badge>
                        </div>
                    </template>
                    <template #cell(enabled)="data">
                        {{ data.value ? "✓" : "" }}
                    </template>
                    <template #cell(schedule)="data">
                        <div v-html="getPrettySchedule(data.item)"></div>
                    </template>
                    <template #cell(completed)="data">
                        <template v-if="data.item.completedBy">
                            {{ formatName(data.item.completedBy) }}<br>
                            {{ formatDate(data.value, true) }}
                        </template>
                    </template>
                </b-table>

                <BottomPagerBar v-if="paging" v-model="pageNumber"
                    :item-name="scheduled ? 'scheduled task' : template ? 'template' : 'task'" show-total
                    :total-rows="totalCount" :per-page="pageSize" :loading="loading"
                    :selected-rows="selectedTasks.length" @change-page-size="pageSize = $event"
                    @clear-selection="clearSelection">
                    <template v-if="!hideCreate" #left>
                        <a v-if="scheduled" href="/maintenance/scheduled-tasks/0" class="btn btn-primary m-0"
                            @click.prevent="activeTaskId = 0">
                            Create Scheduled
                        </a>
                        <a v-else-if="template" href="/maintenance/task-templates/0" class="btn btn-primary m-0"
                            @click.prevent="activeTaskId = 0">
                            Create Template
                        </a>
                        <a v-else
                            :href="!!patient?.id ? `/processing/tasks/0?patientid=${patient.id}` : '/processing/tasks/0'"
                            class="btn btn-primary m-0" @click.prevent="activeTaskId = 0">
                            Create Task
                        </a>
                    </template>
                    <template v-if="selectedTasks.length" #fab>
                        <b-button size="sm" variant="success" @click="markSelectedAsComplete">
                            Mark as Complete
                        </b-button>
                    </template>
                </BottomPagerBar>
            </div>

            <TaskDetails v-if="activeTaskId !== null" :id="activeTaskId" :scheduled="scheduled" :template="template"
                @closed="onModalClose" @saved="onModalSaved" @partial-saved="onPartialSaved" @updated="onModalUpdated" :patient-id="patient?.id"
                @show-merge-modal="onShowMergeModal" />

            <div v-if="false && tasks.length" class="pt-4">
                {{ tasks[0] }}
            </div>
        </component>
        <component :is="card ? 'card' : 'div'" v-else-if="taskId === 0 || !!taskId">
            <TaskDetails :id="taskId" :embed="true" :scheduled="scheduled" :template="template" @saved="onEmbedSave"
                @show-merge-modal="onShowMergeModal" />
        </component>

        <MergeDocumentSelectionModal ref="mergeModal" />
    </div>
</template>

<script lang="ts">
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import HelpPopup from "@/components/HelpPopup.vue";
import { AxiosRequestConfig } from "axios";
import { Component, Vue, Watch, Ref, Prop } from "vue-property-decorator";
import moment from "moment";
import { BTable } from "bootstrap-vue";
import { UserTask, UserTaskRepeatMode, UserTaskStatus } from "@/models/UserTask";
import TaskStatusBadge from "@/components/Tasks/TaskStatusBadge.vue";
import TaskDetails from "@/components/Tasks/TaskDetails.vue";
import MergeDocumentSelectionModal from "@/components/Tasks/MergeDocumentSelectionModal.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Store } from "@/models/Store";
import { Team } from "@/models/Team";
import { Patient } from "@/models/Patient";
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';

interface FilterConfig {
    propertyName: string;
    queryParam: string;
    defaultValue: any;
    parser?: (value: string) => any;
}

@Component({
    components: {
        BottomPagerBar,
        SearchComponent,
        HelpPopup,
        TaskStatusBadge,
        TaskDetails,
        MergeDocumentSelectionModal
    }
})
export default class TasksPage extends Vue {

    protected tasks: UserTask[] = [];

    @Ref("table") protected table!: BTable;
    @Ref("mergeModal") protected mergeModal!: any;
    @Prop() protected taskId?: number;
    @Prop({ default: false }) protected scheduled!: boolean;
    @Prop({ default: false }) protected template!: boolean;
    @Prop({ default: null }) protected patient!: Patient | null;
    @Prop({ default: null }) protected size!: string | null;
    @Prop({ default: null }) protected title!: string | null;
    @Prop({ default: null }) protected blocking!: boolean | null;
    @Prop({ default: false, type: Boolean }) protected onlyShowIfHasTasks!: boolean;
    @Prop({ default: null }) protected icon!: string | null;
    @Prop({ default: false }) protected hideCreate!: boolean;
    @Prop({ default: false }) protected hideFilters!: boolean;
    @Prop({ default: null }) protected titleClass!: string | null;
    @Prop({ default: true }) protected paging!: boolean;
    @Prop({ default: null }) protected taskListFilter!: Function | null;
    @Prop({ default: true, type: Boolean }) protected card!: boolean;
    @Prop({ default: false }) protected filterSearchParams!: boolean;
    @Prop({ default: false }) protected compact!: boolean;

    protected loading = true;
    protected selectMode = "single";

    private pageNumber = 1;
    private pageSize = this.size === "sm" ? 10 : 25;
    private totalCount = 0;

    protected statusFilter: string | number = "active";
    protected dueFilter: number | null = null;
    protected assignedToFilter: string | null = null;
    protected watchedByFilter: string | null = null;
    protected storeFilter: Store | null = new Store();
    protected enabledFilter: boolean | null = true;
    protected searchFilter: string = "";
    protected templateFilter: UserTask | null = new UserTask();
    protected unansweredQuestionsFilter: boolean | null = null;
    // Set by the dashboard "Tasks Completed Today" tile via the completedToday query string. Applied to
    // the request only when the Complete status is selected, so it scopes that list to today's completions.
    protected completedTodayFilter: boolean | null = null;

    // Controls visibility of the "Completed" filter control. It is surfaced only when the page was reached
    // with the completedToday query string (i.e. via the dashboard tile); normal navigation keeps the
    // Tasks page's usual filter set with no completion-date control.
    protected showCompletedTodayFilter = false;

    protected activeTaskId: number | null = null;

    protected sortBy: string = this.scheduled || this.template ? "title" : "startDate";
    protected sortDesc: boolean = false;
    protected hasUserChangedSort: boolean = false;
    protected isProgrammaticSortChange: boolean = false;
    protected teams: Team[] = [];

    protected filterConfigs: FilterConfig[] = [
        { propertyName: 'assignedToFilter', queryParam: 'assignedTo', defaultValue: 'me-team' },
        { propertyName: 'statusFilter', queryParam: 'status', defaultValue: 'active' },
        { propertyName: 'dueFilter', queryParam: 'due', defaultValue: null, parser: (v) => Number(v) },
        { propertyName: 'enabledFilter', queryParam: 'enabled', defaultValue: null, parser: (v) => v === 'true' },
        { propertyName: 'searchFilter', queryParam: 'search', defaultValue: '' },
        { propertyName: 'watchedByFilter', queryParam: 'watchedBy', defaultValue: null },
        { propertyName: 'unansweredQuestionsFilter', queryParam: 'unansweredQuestions', defaultValue: null, parser: (v) => v === 'true' },
        // String(v): $route.query is string on refresh but keeps the raw boolean on router.push
        // navigation (the dashboard tile), so normalize both true and "true" to the same result.
        { propertyName: 'completedTodayFilter', queryParam: 'completedToday', defaultValue: null, parser: (v) => String(v) === 'true' }
    ];

    UserTaskStatus = UserTaskStatus;
    moment = moment;

    get pageMode() {
        return `${this.template ? 'template' : ''}${this.scheduled ? 'scheduled' : ''}`;
    }

    @Watch("pageMode")
    onPageModeChange() {
        // Clear search filter when switching between Task Templates and Scheduled Tasks
        this.searchFilter = "";

        // Remove search parameter from URL if filterSearchParams is enabled
        if (this.filterSearchParams) {
            const query = { ...this.$route.query };
            delete query.search;

            this.$router.replace({
                path: this.$route.path,
                query
            }).catch(() => {
                // Ignore navigation duplicated errors
            });
        }
    }

    mounted() {
        if (!this.taskId) {
            if (this.size !== "sm") {
                this.pageSize = getPageSizePreference(this, 'pageSize_Tasks', 25);
            }
            // Only surface the "Completed" filter when arriving via the completedToday query string.
            this.showCompletedTodayFilter = this.filterSearchParams
                && this.$route?.query?.completedToday !== undefined;
            this.handleInitialFilters();
            this.setupFilterWatchers();
            this.getTasks();
            this.getTeams();
        }
    }

    updateUrlQuery(filterName: string, value: any, defaultValue: any = null) {
        if (!this.filterSearchParams) return;
        const query = { ...this.$route.query };

        if (value !== defaultValue && value !== null && value !== '') {
            query[filterName] = value;
        } else {
            delete query[filterName];
        }

        this.$router.replace({
            path: this.$route.path,
            query
        }).catch(() => {
            // Ignore navigation duplicated errors
        });
    }

    setupFilterWatchers() {
        if (!this.filterSearchParams) return;
        this.filterConfigs.forEach(config => {
            this.$watch(config.propertyName, (newValue: any) => {
                this.updateUrlQuery(config.queryParam, newValue, config.defaultValue);
            });
        });
    }

    @Watch("storeFilter")
    onStoreFilterChange(newValue: Store | null) {
        if (!this.filterSearchParams) return;
        this.updateUrlQuery('storeId', newValue?.id || null);
    }

    @Watch("templateFilter")
    onTemplateFilterChange(newValue: Store | null) {
        if (!this.filterSearchParams) return;
        this.updateUrlQuery('templateId', newValue?.id || null);
    }

    handleInitialFilters() {
        if (!this.filterSearchParams) return;

        const query = this.$route.query;

        this.filterConfigs.forEach(config => {
            const queryValue = query[config.queryParam] as string;
            if (queryValue !== undefined) {
                const value = config.parser ? config.parser(queryValue) : queryValue;
                (this as any)[config.propertyName] = value;
            }
        });

        // Handle special cases
        if (query.storeId) {
            this.storeFilter = Object.assign(new Store(), { id: Number(query.storeId) });
        }
        if (query.templateId) {
            this.templateFilter = Object.assign(new UserTask, { id: Number(query.templateId) });
        }
    }

    get getTaskParams() {
        const params = {
            pageSize: this.paging ? this.pageSize : null,
            pageNumber: this.paging ? this.pageNumber - 1 : null,
            assigned: this.scheduled || this.template || this.patient?.id ?
                undefined : (this.assignedToFilter === "everyone" ? null : !this.assignedToFilter ? "me-team" : this.assignedToFilter),
            watched: this.scheduled || this.template || this.patient?.id ? undefined : this.watchedByFilter,
            status: this.scheduled || this.template ? undefined : this.statusFilter,
            due: this.scheduled || this.template || this.patient?.id ? undefined : this.dueFilter,
            storeId: this.storeFilter?.id,
            scheduled: this.scheduled,
            template: this.template,
            enabled: this.scheduled || this.template ? this.enabledFilter : undefined,
            sortBy: this.sortBy,
            sortDirection: this.sortDesc ? "D" : "A",
            patientId: this.patient?.id,
            searchTerm: this.searchFilter,
            createdByTemplateId: this.templateFilter?.id,
            blocking: this.blocking,
            hasUnansweredQuestions: this.scheduled || this.template ? undefined : this.unansweredQuestionsFilter,
            // Only scope to today's completions when viewing the Complete status (statusFilter may be the
            // numeric enum or its string form off the URL); otherwise it would empty out the active list.
            completedToday: this.scheduled || this.template || this.patient?.id
                || Number(this.statusFilter) !== UserTaskStatus.complete ? undefined : this.completedTodayFilter
        };

        return params;
    }

    @Watch("getTaskParams")
    async getTasks() {
        // Skip API call if we're in the middle of a programmatic sort change
        // to prevent multiple API calls with different sort parameters
        if (this.isProgrammaticSortChange) {
            return;
        }

        try {
            this.loading = true;
            if (this.pageNumber === 1) {
                this.tasks = [];
            }
            const config: AxiosRequestConfig = {
                params: this.getTaskParams
            };
            const response = await this.$http.get("/task/", config);
            this.tasks = response.data.tasks;
            this.totalCount = response.data.total;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
            this.tasks = [];
            this.totalCount = 0;
        } finally {
            this.loading = false;
        }
    }

    @Watch("assignedToFilter")
    updateAssignedToQuery(newValue: string | null) {
        if (!this.filterSearchParams) return;

        const query = { ...this.$route.query };

        if (newValue && newValue !== 'me-team') {
            query.assignedTo = newValue;
        } else {
            delete query.assignedTo;
        }

        this.$router.replace({
            path: this.$route.path,
            query
        }).catch(() => {

        });
    }

    async getTeams() {
        try {
            const response = await this.$http.get<Team[]>("/user/teams");
            this.teams = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    get filteredTasks() {
        if (!this.taskListFilter) return this.tasks;
        return this.tasks.filter(t => this.taskListFilter!(t));
    }

    @Watch("pageSize")
    @Watch("sortBy")
    @Watch("sortDesc")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    @Watch("pageSize")
    onPageSizePreferenceChanged() {
        if (this.size !== "sm") {
            savePageSizePreference(this, 'pageSize_Tasks', this.pageSize);
        }
    }

    @Watch("sortBy")
    @Watch("sortDesc")
    onSortChange() {
        // Track that user has manually changed the sort
        // This prevents auto-updating sort when status filter changes
        // Skip tracking if this is a programmatic change (e.g., from status filter change)
        if (!this.isProgrammaticSortChange) {
            this.hasUserChangedSort = true;
        }
    }

    @Watch("statusFilter")
    async onStatusFilterChange(newValue: string | number) {
        // When switching to 'complete' status and user hasn't customized sort,
        // automatically sort by updated date (most recent first)
        if (newValue === UserTaskStatus.complete && !this.hasUserChangedSort) {
            this.isProgrammaticSortChange = true;
            this.sortBy = 'updated';
            this.sortDesc = true;
            // Wait for next tick to ensure both sort changes are applied before allowing getTasks to run
            await this.$nextTick();
            this.isProgrammaticSortChange = false;
            // Now manually trigger getTasks with the correct parameters
            this.getTasks();
        } else if (newValue === 'active' && !this.hasUserChangedSort) {
            // When switching back to active, reset to default sort
            this.isProgrammaticSortChange = true;
            this.sortBy = 'startDate';
            this.sortDesc = false;
            await this.$nextTick();
            this.isProgrammaticSortChange = false;
            // Now manually trigger getTasks with the correct parameters
            this.getTasks();
        }
    }

    get fields() {
        const userName = this.$user?.userName?.toLowerCase();
        return [
            { key: "lock", label: "" },
            {
                key: "watching",
                label: "",
                hidden: this.scheduled || this.template,
                formatter: (_: any, __: string, item: UserTask) => {
                    if (item.watchers.find(w => w.userName === userName))
                        return "me";
                    if (item.watchers.filter(w => w.teamId)
                        .map(w => this.teams.find(t => t.id === w.teamId))
                        .flatMap(t => t?.users).find(u => u?.userName?.toLowerCase() === userName))
                        return "team";
                    return undefined;
                },
                class: "watching"
            },
            {
                key: "id",
                sortable: true
            },
            {
                key: "enabled",
                hidden: !this.scheduled && !this.template,
                sortable: true,
                class: "enabled-col text-center"
            },
            { key: "title", sortable: true, class: "title-col" },
            { key: "storeName", label: "Store", sortable: true, hidden: this.compact },
            {
                key: "assignments",
                label: "Assigned To",
                sortable: true
            },
            {
                key: "startDate",
                formatter: (value: Date) => { return this.formatDateOnly(value) },
                sortable: true, hidden: this.compact || this.scheduled || this.template
            },
            {
                key: "dueDate",
                formatter: (value: Date, _: string, item: UserTask) => {
                    return this.formatDueDate(value, item.dueAtTime);
                },
                sortable: true,
                hidden: this.compact || this.scheduled || this.template
            },
            {
                key: "dates",
                label: "Due Date",
                sortable: true,
                sortByFormatted: true,
                formatter: (value: any, key: string, item: UserTask) => {
                    return item.dueDate;
                },
                hidden: !this.compact || this.scheduled || this.template
            },
            {
                key: "status",
                sortable: true,
                hidden: this.scheduled || this.template
            },
            {
                key: "patients",
                label: "Linked Patients",
                hidden: this.scheduled || this.template
            },
            {
                key: "prescriptions",
                label: "Linked Prescriptions",
                hidden: this.scheduled || this.template
            },
            {
                key: "schedule",
                hidden: !this.scheduled,
                class: "schedule-col"
            },
            {
                key: "nextScheduled",
                formatter: (value: Date) => { return this.formatDateOnly(value) },
                hidden: !this.scheduled,
                class: "next-schedule-col"
            },
            {
                key: "updated",
                label: "Last Activity",
                formatter: (value: Date) => { return this.formatDate(value) },
                hidden: this.scheduled || this.template || !!this.patient,
                sortable: true
            },
            {
                key: "completed",
                hidden: this.scheduled || this.template || this.statusFilter === "active"
            }
        ].filter(f => f.hidden !== true);
    }

    get assignedToFilterOptions() {
        return [
            { text: 'Myself & My Teams', value: null, title: 'Show tasks assigned to me or any of my teams' },
            { text: 'Myself', value: 'me', title: 'Show only tasks assigned directly to me' },
            { text: 'Everyone', value: 'everyone', title: 'Show all tasks regardless of assignment' },
            { text: 'Provider', value: 'prescriber', title: 'Show tasks assigned to any provider/prescriber' },
            { text: 'Patient', value: 'patient', title: 'Show tasks assigned to any patient' },
            { text: 'User', value: 'user', title: 'Show tasks assigned to an individual Scriptly user' },
            { text: 'Brand Portal User', value: 'brand-portal', title: 'Show tasks assigned to any brand portal user' },
        ];
    }

    get watchedByFilterOptions() {
        return [
            { text: '', value: null },
            { text: 'Myself & My Teams', value: 'me-team' },
            { text: 'Myself', value: 'me' },
        ]
    }

    get statusFilterOptions() {
        return [
            { text: 'Active', value: 'active' }, // 'active' (New + InProgress backend)
            { text: 'New', value: UserTaskStatus.new },
            { text: 'In Progress', value: UserTaskStatus.inProgress },
            { text: 'Complete', value: UserTaskStatus.complete },
            { text: 'Expired', value: UserTaskStatus.expired },
            { text: 'Cancelled', value: UserTaskStatus.cancelled },
            { text: 'All Statuses', value: 'all' }
        ];
    }

    get dueFilterOptions() {
        return [
            { text: 'Any Due Date', value: null },
            { text: 'This Week', value: 7 },
            { text: 'Today', value: 1 },
            { text: 'Past Due', value: 0 }
        ];
    }

    get completedTodayFilterOptions() {
        return [
            { text: 'Any Completion Date', value: null },
            { text: 'Completed Today', value: true }
        ];
    }

    get enabledFilterOptions() {
        return [
            { text: 'Enabled', value: true },
            { text: 'Disabled', value: false },
            { text: 'All', value: null }
        ]
    }

    get unansweredQuestionsFilterOptions() {
        return [
            { text: 'All', value: null },
            { text: 'Has Required Unanswered', value: true },
            { text: 'All Required Answered', value: false }
        ]
    }

    formatDate(date: any, withTime: boolean = false) {
        if (!date) return "";
        return moment.utc(date).local().format(withTime ? "LLL" : "LL");
    }

    formatDateOnly(date: any) {
        if (!date) return "";
        return moment.utc(date).format("LL");
    }

    formatDueDate(date: any, dueAtTime: boolean) {
        if (!date) return "";
        if (dueAtTime) {
            return moment.utc(date).local().format("LLL");
        }
        return moment.utc(date).format("LL");
    }

    protected selectedTasks: UserTask[] = [];

    onSelect(items: UserTask[]) {
        if (items.length && this.selectMode === 'single') {
            this.activeTaskId = items[0].id;
            return;
        }
        if (!items.length) {
            this.selectMode = 'single';
        }
        this.selectedTasks = items;
    }

    onModalSaved() {
        this.onModalClose();
        this.getTasks();
        this.$emit('task-updated');
    }

    onPartialSaved() {
        // Refresh task list without closing the modal
        this.getTasks();
        this.$emit('task-updated');
    }

    onModalUpdated() {
        this.getTasks();
        this.$emit('task-updated');
    }

    onModalClose() {
        if (this.selectMode === 'single') {
            this.table?.clearSelected();
        }
        this.activeTaskId = null;
        this.getTasks();
    }

    onEmbedSave(task: UserTask) {
        this.$router.replace({
            name: this.scheduled ? "ScheduledTasks" : "Tasks",
            params: { taskId: task.id.toString() }
        });
    }

    isPastDue(task: UserTask) {
        if (task.status === UserTaskStatus.complete
            || task.status === UserTaskStatus.cancelled
            || task.status === UserTaskStatus.expired) return false;
        const dueDate = moment.utc(task.dueDate);
        const currentDate = moment.utc();
        if (task.dueAtTime) {
            return dueDate.isBefore(currentDate);
        }
        return dueDate.startOf("day").isBefore(currentDate.startOf("day"));
    }

    isFutureStart(task: UserTask) {
        if (!task.startDate) return false;
        const startDate = moment.utc(task.startDate);
        const currentDate = moment.utc();
        return startDate.startOf("day").isAfter(currentDate.startOf("day"));
    }

    getPrettySchedule(task: UserTask) {
        const schedule = task.repeatSchedule ?? "";

        switch (task.repeatMode) {
            case UserTaskRepeatMode.dayInterval: {
                if (schedule === "1") return "<strong>Everyday</strong>";
                if (schedule === "2") return "Every <strong>Other</strong> Day";
                return `Every <strong>${schedule}</strong> Days`;
            }
            case UserTaskRepeatMode.daysOfWeek:
                {
                    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                    const days = dayNames.filter((_, i) => schedule.includes(i.toString()));

                    if (days.length === 7) return "<strong>Everyday</strong>";
                    if (days.length === 5 && !days.includes("Sunday") && !days.includes("Saturday")) {
                        return "Every <strong>Weekday</strong>";
                    }
                    return `Every <strong>${days.join(", ")}</strong>`;
                }
            case UserTaskRepeatMode.daysOfMonth:
                {
                    const days = schedule.split(",").map((d: string) => this.getDayWithOrdinal(Number(d)));
                    if (days.length === 31) return "<strong>Everyday</strong>";
                    if (days.length === 2) return `Every <strong>${days.join(" and ")}</strong> of the month`;
                    return `Every <strong>${days.join(", ")}</strong> of the month`;
                }
        }
    }

    getDayWithOrdinal(day: number) {
        const lastDigit = day % 10;
        const lastTwoDigits = day % 100;

        if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
            return `${day}th`;
        }

        switch (lastDigit) {
            case 1:
                return `${day}st`;
            case 2:
                return `${day}nd`;
            case 3:
                return `${day}rd`;
            default:
                return `${day}th`;
        }
    }

    formatName(name: string) {
        if (name.includes("/")) return name.split("/")[1];
        if (name.includes("\\")) return name.split("\\")[1];
        return name.split("@")[0];
    }

    clearSelection() {
        this.table?.clearSelected();
    }

    async markSelectedAsComplete() {
        try {
            const confirm = await this.$bvModal.msgBoxConfirm("Are you sure you want to mark all selected tasks as complete?",
                {
                    title: "Mark All as Complete?",
                    noFade: true,
                    centered: true
                }
            );

            if (!confirm) return;
            await this.$http.post("/task/bulk-complete/", this.selectedTasks.map(t => t.id));

            this.getTasks();

            this.$notification(NotificationOptions.success("Marked selected tasks as complete"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    onShowMergeModal(event: { taskId: number, documents: any[] }) {
        this.mergeModal.show(event.taskId);
    }
}

</script>

<style scoped lang="scss">
::v-deep(.table-responsive) {
    overflow-x: auto;
    overflow-y: visible !important;
    -webkit-overflow-scrolling: touch;
}

::v-deep(.compact-table) {
    font-size: 0.875rem;

    td, th {
        padding: 0.5rem;
    }

    .badges {
        gap: 3px;
    }

    .badge {
        font-size: 0.65rem;
        padding: 0.15rem 0.35rem;
    }
}

.badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.filters {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 8px;
    margin-bottom: 15px;

    >* {
        margin-bottom: 0;
    }

    ::v-deep(.btn) {
        margin-top: 0;
        margin-left: -2px;
        opacity: .8;
        border-color: rgba(black, .3);
    }

    ::v-deep(.btn:first-child) {
        margin-left: 0;
    }

    ::v-deep(.btn.active) {
        background: rgba(#009cde, .8) !important;
        color: white !important;
        border-color: rgba(black, .4) !important;
        opacity: 1;
    }
}

::v-deep(.action-col) {
    width: 50px;
}

::v-deep(.enabled-col) {
    width: 50px;
}

::v-deep(.schedule-col) {
    width: 300px;
}

::v-deep(.next-schedule-col) {
    width: 160px;
}

::v-deep(tr) {
    .select {
        display: none;
    }

    .select.fa-check-square {
        display: block;
    }

    &.b-table-row-selected .fa-eye {
        display: none;
    }

    &:hover {
        .select {
            display: block;
        }

        .fa-eye {
            display: none;
        }
    }
}

::v-deep(.watching) {
    width: 32px;
    min-width: 32px;
    max-width: 32px;
}

::v-deep(.title-col) {
    min-width: 200px;
}
</style>
